import { useDispatch, useSelector } from 'react-redux';
import { useRef, useState } from 'react';

import type { Text } from 'src/__generated__/graphqlTypes';
import { createTicketProxy } from 'src/api/externalApi';
import type { Store } from 'app-redux/types/storeTypes';
import { setLoadingState } from 'app-redux/actions/appActions';
import { Logger } from 'lib/logger';
import type { ZendeskTicketRequestData } from 'types/objectTypes';
import { getCountriesListProxy } from 'src/api/countriesApi';

import { FeedbackFormInterfaceStateValues } from '../config/declarations';

export const useHandlers = ({
  showModal,
  clearForm,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((store: Store) => store.server.auth.user);
  const viewerDevice = useSelector((store: Store) => store.server.app.viewerDevice);
  const userAgent = useSelector((store: Store) => store.client.app.userAgent);

  const handleSubmitForm = async (values: FeedbackFormInterfaceStateValues) => {
    const zendeskData: ZendeskTicketRequestData = {
      country: values.country.value,
      customerId: user?.customerId,
      emailAddressOnAccount: user?.email,
      emailAddressProvided: values.email.value,
      message: values.message.value,
      name: values.name.value,
      phoneNumber: values.phoneNumber.value,
      requestType: values.requestType.value,
      platform: viewerDevice,
      browser: userAgent?.browser?.name,
      browserVersion: userAgent?.browser?.version,
    };

    try {
      dispatch(setLoadingState(true));
      await createTicketProxy(zendeskData);
      showModal();
      clearForm();
    } catch (e: any) {
      Logger.error(e.data);
    } finally {
      dispatch(setLoadingState(false));
    }
  };

  return handleSubmitForm;
};

export const useCountries = () => {
  const fetchedCountries = useRef<Array<Text>>([]);
  const [countries, setCountries] = useState<Array<Text>>(fetchedCountries.current);
  const dispatch = useDispatch();

  const fetchCountries = async () => {
    try {
      dispatch(setLoadingState(true));

      if (fetchedCountries.current.length > 0) {
        return;
      }

      const countries = await getCountriesListProxy();
      fetchedCountries.current = countries;
      setCountries(countries);
    } catch (e: any) {
      Logger.error(e);
    } finally {
      dispatch(setLoadingState(false));
    }
  };

  const searchInCountries = (text: string) => {
    setCountries(fetchedCountries.current
      .filter((country) => country.text?.toLowerCase()
        .includes(text?.toLowerCase()!)));
  };

  return { countries, fetchCountries, searchInCountries };
};
